import * as React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import Description from "@v1/components/designSystem/label/Description";
import DeprecatedLabel from "@v1/components/designSystem/label/Label";

type TextareaProps = {
  id?: string;
  value?: string;
  placeholder?: string;
  autoFocus?: boolean;
  isDisabled?: boolean;
  label?: React.ReactNode;
  size?: "sm" | "base";
  textLength?: "long" | "short";
  description?: React.ReactNode;
  onChange(value: string): void;
  onBlur?(): void;
  onFocus?(): void;
  onEnter?(): void;
  onEscape?(): void;
  onKeyDown?(e: React.KeyboardEvent): void;
  onKeyUp?(e: React.KeyboardEvent): void;
  onKeyPress?(e: React.KeyboardEvent): void;
  onMouseDown?(): void;
  onMouseUp?(): void;
  onMouseMove?(): void;
  UNSAFE_className?: string;
  layoutClassName?: string;
  font?: "normal" | "mono";
  style?: React.CSSProperties;
  ref?: React.RefObject<HTMLTextAreaElement>;
  maxLength?: number;
};

/**
 * @deprecated Use Textarea from @replo/design-system/components/textarea/Textarea instead.
 *
 * Migration notes:
 * - isDisabled -> disabled
 * - textLength="long"|"short" -> variant="long"|"short"
 * - layoutClassName -> className
 * - label/description props -> use Field component wrapper
 * - onEnter/onEscape -> implement via onKeyDown
 * - onChange(value: string) -> onChange(e: React.ChangeEvent<HTMLTextAreaElement>)
 */
const DeprecatedTextarea = ({
  id,
  value = "",
  placeholder = "",
  autoFocus = false,
  isDisabled = false,
  label,
  size = "base",
  textLength = "long",
  description,
  onChange,
  onBlur,
  onFocus,
  onEnter,
  onEscape,
  onKeyDown,
  onKeyUp,
  onKeyPress,
  onMouseDown,
  onMouseUp,
  onMouseMove,
  UNSAFE_className,
  layoutClassName,
  font = "normal",
  style,
  ref,
  maxLength,
}: TextareaProps) => {
  function _onFocusInput() {
    return onFocus?.();
  }

  function _onBlurInput() {
    return onBlur?.();
  }

  function _onChangeInput(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const trimmedValue =
      maxLength !== undefined
        ? e.target.value.slice(0, Math.max(0, maxLength))
        : e.target.value;
    onChange?.(trimmedValue);
  }

  function _onKeyPressInput(e: React.KeyboardEvent) {
    if (e.key === "Enter" && onEnter && !e.shiftKey) {
      e.preventDefault();
      onEnter();
    }
    if (e.key === "Escape" && onEscape) {
      onEscape?.();
    }
    return onKeyPress?.(e);
  }

  const internalClassNames = twMerge(
    "resize-none rounded-md bg-sheet p-3 text-default outline-hidden transition-all duration-300 placeholder-placeholder disabled:cursor-not-allowed disabled:opacity-50 border-0.5 border-component-border",
    "disabled:opacity-50 disabled:cursor-not-allowed",
    "focus-within:ring-1 focus-within:ring-primary focus-within:border-transparent focus-within:ring-inset",
    "[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
    size === "sm" && "text-xs",
    size === "base" && "text-sm",
    size === "sm" && textLength === "short" && "h-[48px]",
    size === "sm" && textLength === "long" && "h-[80px]",
    size === "base" && textLength === "short" && "h-[64px]",
    size === "base" && textLength === "long" && "h-[104px]",
    font === "mono" && "font-mono",
  );

  return (
    // NOTE (Patrick 2025-01-06): This container needs leading-0 otherwise the textarea will have empty space below it, causing extra gap between the Description
    <div className="leading-0 flex w-full flex-col gap-1.5 rounded-md">
      {label && <DeprecatedLabel size={size} label={label} />}
      <textarea
        id={id}
        className={twMerge(
          internalClassNames,
          layoutClassName,
          UNSAFE_className,
        )}
        style={style}
        value={value}
        placeholder={placeholder}
        autoFocus={autoFocus}
        disabled={isDisabled}
        onChange={_onChangeInput}
        onBlur={_onBlurInput}
        onFocus={_onFocusInput}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        onKeyPress={_onKeyPressInput}
        onMouseDown={onMouseDown}
        onMouseUp={onMouseUp}
        onMouseMove={onMouseMove}
        ref={ref}
      />
      {description && <Description description={description} size={size} />}
    </div>
  );
};

export { DeprecatedTextarea };
